import {
    Evaluatie,
    EvaluatiePunt as EvaluatiePuntType,
    MIDDELEN_INZET_VALUES,
    MiddelInzet,
    useExportEvaluatie,
    usePutEvaluatie,
} from "@/entities/evaluatie";
import {
    Button,
    CheckboxFormField,
    ErrorDisplay,
    Form,
    FormFieldInput,
    Icon,
    SelectFormField,
    SelectOption,
    Separated,
    TextAreaFormFieldProps,
    TextLink,
} from "@/shared/components";
import { useDebouncedCallback } from "@/shared/hooks/use-debounced-callback";
import { optionalFormEnum, optionalFormString } from "@/shared/utils/zod-helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { buildEvaluatiePayload } from "../build-evaluatie-payload";
import { IC_DOC_ENVIRONMENT } from "../constants";
import { EvaluatiePunt } from "./evaluatie-punt";
import { EvaluatieSectie } from "./evaluatie-sectie";

const schema = z.object({
    geenVsmToegedeeld: z.boolean(),
    heeftKhrStatus: z.boolean(),
    icDocLink: z.string().trim(),
    ingezetteMiddelen: z.object({
        tvv: optionalFormEnum(MIDDELEN_INZET_VALUES),
        faciliteiten: optionalFormEnum(MIDDELEN_INZET_VALUES),
        vos: optionalFormEnum(MIDDELEN_INZET_VALUES),
    }),
    evaluatiePunten: z.object({
        bijsturingTreindienst: optionalFormString(),
        begeleidingOmreis: optionalFormString(),
        alternatiefVervoer: optionalFormString(),
        klantopvang: optionalFormString(),
        personeel: optionalFormString(),
        interneBeleving: optionalFormString(),
        voorgesteldeActiepunten: optionalFormString(),
        klantbeleving: optionalFormString(),
    }),
});

type EvaluatiePunten = {
    name: EvaluatiePuntType;
    label: string;
    placeholder: string;
};

const evaluatiePunten: EvaluatiePunten[] = [
    {
        name: "bijsturingTreindienst",
        label: Evaluatie.getEvaluatiePuntLabel("bijsturingTreindienst"),
        placeholder: "Beschrijf de bijsturing treindienst",
    },
    {
        name: "begeleidingOmreis",
        label: Evaluatie.getEvaluatiePuntLabel("begeleidingOmreis"),
        placeholder: "Beschrijf de begeleiding omreis",
    },
    {
        name: "alternatiefVervoer",
        label: Evaluatie.getEvaluatiePuntLabel("alternatiefVervoer"),
        placeholder: "Beschrijf het alternatief vervoer",
    },
    { name: "klantopvang", label: Evaluatie.getEvaluatiePuntLabel("klantopvang"), placeholder: "Beschrijf de klantopvang" },
    { name: "personeel", label: Evaluatie.getEvaluatiePuntLabel("personeel"), placeholder: "Beschrijf het personeel" },
    {
        name: "interneBeleving",
        label: Evaluatie.getEvaluatiePuntLabel("interneBeleving"),
        placeholder: "Beschrijf de interne beleving",
    },
    {
        name: "voorgesteldeActiepunten",
        label: Evaluatie.getEvaluatiePuntLabel("voorgesteldeActiepunten"),
        placeholder: "Beschrijf de voorgestelde actiepunten",
    },
    { name: "klantbeleving", label: Evaluatie.getEvaluatiePuntLabel("klantbeleving"), placeholder: "Beschrijf de klantbeleving" },
] as const;

type KHREvaluatieFormInput = z.input<typeof schema>;

type KHREvaluatieProps = {
    dossierId: string;
    evaluatie: Evaluatie | null;
    readOnly: boolean;
};

export function KHREvaluatie({ dossierId, evaluatie, readOnly }: KHREvaluatieProps) {
    const [validationError, setValidationError] = useState<Error | null>(null);
    const form = useForm<KHREvaluatieFormInput>({
        resolver: zodResolver(schema),
        defaultValues: {
            geenVsmToegedeeld: evaluatie?.geenVsmToegedeeld ?? false,
            heeftKhrStatus: evaluatie?.heeftKhrStatus ?? false,
            icDocLink: evaluatie?.icDocLink ?? "",
            ingezetteMiddelen: {
                tvv: evaluatie?.ingezetteMiddelen?.tvv ?? "",
                faciliteiten: evaluatie?.ingezetteMiddelen?.faciliteiten ?? "",
                vos: evaluatie?.ingezetteMiddelen?.vos ?? "",
            },
            evaluatiePunten: {
                bijsturingTreindienst: evaluatie?.evaluatiePunten?.bijsturingTreindienst ?? "",
                begeleidingOmreis: evaluatie?.evaluatiePunten?.begeleidingOmreis ?? "",
                alternatiefVervoer: evaluatie?.evaluatiePunten?.alternatiefVervoer ?? "",
                klantopvang: evaluatie?.evaluatiePunten?.klantopvang ?? "",
                personeel: evaluatie?.evaluatiePunten?.personeel ?? "",
                interneBeleving: evaluatie?.evaluatiePunten?.interneBeleving ?? "",
                voorgesteldeActiepunten: evaluatie?.evaluatiePunten?.voorgesteldeActiepunten ?? "",
                klantbeleving: evaluatie?.evaluatiePunten?.klantbeleving ?? "",
            },
        },
    });

    const putEvaluatie = usePutEvaluatie();
    const exportEvaluatie = useExportEvaluatie();

    const handleChange = () => {
        // parse via schema to trimmed values. With the current schema, validation should always succeed.
        const parsed = schema.safeParse(form.getValues());

        if (!parsed.success) {
            setValidationError(new Error("Validatiefout"));
            return;
        }

        const { geenVsmToegedeeld, heeftKhrStatus, icDocLink, ingezetteMiddelen, evaluatiePunten } = parsed.data;

        const nextEvaluatie = buildEvaluatiePayload(evaluatie, {
            geenVsmToegedeeld,
            heeftKhrStatus,
            icDocLink,
            ingezetteMiddelen,
            evaluatiePunten,
        });

        putEvaluatie.mutate({ dossierId, ...nextEvaluatie });
    };

    const icDocLink = form.watch("icDocLink");
    const debouncedHandleChange = useDebouncedCallback(handleChange);

    return (
        <Form {...form}>
            <form onChange={readOnly ? undefined : debouncedHandleChange}>
                <fieldset disabled={readOnly}>
                    <Separated>
                        <EvaluatieSectie
                            title="Klanthinderrapportage (KHR)"
                            description="Interne rapportage voor klanthinder registratie."
                            headingLevel="h2"
                            action={
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="s"
                                    icon="download"
                                    iconStrokeWidth="thick"
                                    onClick={() => exportEvaluatie.mutate({ dossierId })}
                                >
                                    Exporteren
                                </Button>
                            }
                        >
                            {exportEvaluatie.error && <ErrorDisplay error={exportEvaluatie.error} />}
                            <div className="flex items-start gap-4">
                                <CheckboxFormField form={form} name="geenVsmToegedeeld" label="Geen VSM toegedeeld" readOnly={readOnly} />
                                <div>
                                    <CheckboxFormField
                                        form={form}
                                        name="heeftKhrStatus"
                                        label="Geef deze calamiteit de KHR-status"
                                        readOnly={readOnly}
                                    />
                                    <p className="text-s text-content-subtle col-start pl-6">
                                        De KHR-status wordt bereikt bij het opheffen van 5 of meer treinen op een route.
                                    </p>
                                </div>
                            </div>
                        </EvaluatieSectie>
                        <EvaluatieSectie
                            title="IC-doc koppeling"
                            description="Koppel het dossier aan IC-doc na het uploaden van de PDF."
                            action={
                                readOnly ? undefined : (
                                    <TextLink href={IC_DOC_ENVIRONMENT} icon="external" target="_blank" className="text-s">
                                        Naar IC-doc
                                    </TextLink>
                                )
                            }
                        >
                            {!readOnly && (
                                <div className="flex items-end gap-1">
                                    <FormFieldInput
                                        form={form}
                                        name="icDocLink"
                                        label="IC-doc link"
                                        placeholder="Plak hier de IC-doc link"
                                        className="flex-1"
                                        readOnly={readOnly}
                                        maxLength={1000}
                                    />
                                    <a
                                        href={icDocLink}
                                        onClick={(e) => {
                                            if (!icDocLink) {
                                                e.preventDefault();
                                            }
                                        }}
                                        target="_blank"
                                        className="border-input-default text-content-subtle rounded-m flex border p-1.75"
                                        data-testid="ic-doc-deeplink"
                                    >
                                        <Icon name="external" />
                                    </a>
                                </div>
                            )}
                            {readOnly && (
                                <div className="grid gap-2">
                                    <span className="text-content-emphasis font-medium">IC-Doc</span>
                                    <p className="text-s text-content-subtle">
                                        {evaluatie?.icDocLink ? (
                                            <TextLink href={evaluatie.icDocLink} icon="external" target="_blank">
                                                Link naar IC-doc dossier
                                            </TextLink>
                                        ) : (
                                            "Nog niet toegevoegd"
                                        )}
                                    </p>
                                </div>
                            )}
                        </EvaluatieSectie>
                        <EvaluatieSectie title="Middeleninzet" description="Registreer de status van ingezette middelen.">
                            <div className="grid grid-cols-3 gap-4">
                                <MiddelenInzet label="TVV" form={form} name="ingezetteMiddelen.tvv" readOnly={readOnly} />
                                <MiddelenInzet label="Faciliteiten" form={form} name="ingezetteMiddelen.faciliteiten" readOnly={readOnly} />
                                <MiddelenInzet label="Bemensing" form={form} name="ingezetteMiddelen.vos" readOnly={readOnly} />
                            </div>
                        </EvaluatieSectie>
                        <EvaluatieSectie
                            title="Evaluatiepunten"
                            description="Selecteer de relevante evaluatiepunten en voeg indien nodig een toelichting toe."
                        >
                            <div className="grid gap-2">
                                {evaluatiePunten.map((punt) => (
                                    <EvaluatiePunt
                                        key={punt.name}
                                        form={form}
                                        name={`evaluatiePunten.${punt.name}`}
                                        label={punt.label}
                                        placeholder={punt.placeholder}
                                        defaultOpen={!!evaluatie?.evaluatiePunten?.[punt.name]}
                                        onClear={handleChange}
                                        readonly={readOnly}
                                    />
                                ))}
                            </div>
                        </EvaluatieSectie>
                    </Separated>
                </fieldset>
            </form>
            {putEvaluatie.error && <ErrorDisplay error={putEvaluatie.error} />}
            {validationError && <ErrorDisplay error={validationError} />}
        </Form>
    );
}

const middelenInzetOptions: SelectOption<MiddelInzet>[] = [
    {
        value: "NietBesteld",
        label: "Niet besteld",
    },
    {
        value: "BesteldNietIngezet",
        label: "Besteld - niet ingezet",
    },
    {
        value: "Ingezet",
        label: "Ingezet",
    },
];

type MiddelenInzetProps = Pick<TextAreaFormFieldProps<KHREvaluatieFormInput>, "form" | "name" | "label"> & { readOnly?: boolean };

function MiddelenInzet({ form, name, label, readOnly }: MiddelenInzetProps) {
    return <SelectFormField form={form} name={name} options={middelenInzetOptions} label={label} readOnly={readOnly} />;
}
